/**
 * Where a drawn element sits, and what a point on the page is pointing at.
 *
 * Positions come from the browser, not from this package: the engine's SVG is scaled,
 * translated and possibly zoomed by whatever surrounds it, and the only thing that knows
 * the result is the layout engine that produced it. So the host is asked, through the
 * smallest possible interface -- one lookup and one rectangle -- which keeps this module
 * free of a DOM dependency and testable with a plain object.
 *
 * Boxes are returned relative to the container they were measured in, not to the viewport,
 * so that scrolling the score does not invalidate them. The container's own rectangle is
 * subtracted once, at measuring time.
 *
 * They are also in the container's own units, not the screen's. A host may zoom the score
 * with a transform around the container, and the browser reports rectangles after it; but
 * whatever is drawn from the boxes is drawn inside that same container, where the transform
 * applies again. So the container's scale -- its drawn width over its laid-out width -- is
 * divided out, and a box stays right at every zoom without being measured again. A point is
 * brought into the same units by {@link pointIn} before it is compared with one.
 */

/** A rectangle, in the same units the host measures in. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** As much of a DOM element as measuring needs. */
export interface Measurable {
  getBoundingClientRect(): Box;
}

/** As much of a DOM container as measuring needs; a real `HTMLElement` satisfies it. */
export interface BoxHost extends Measurable {
  /** The width the container was laid out at, before any transform around it. */
  readonly offsetWidth: number;
  querySelector(selectors: string): Measurable | null;
}

/** How much a transform around `host` scales it on screen; 1 where nothing does. */
function scaleOf(host: BoxHost, drawn: Box): number {
  return host.offsetWidth > 0 && drawn.width > 0 ? drawn.width / host.offsetWidth : 1;
}

/** A point on screen, such as a pointer's, relative to `host` and in its own units. */
export function pointIn(host: BoxHost, x: number, y: number): Point {
  const origin = host.getBoundingClientRect();
  const scale = scaleOf(host, origin);
  return { x: (x - origin.x) / scale, y: (y - origin.y) / scale };
}

/**
 * Measure drawn elements inside a container.
 *
 * Ids are matched with an attribute selector rather than `#id`, because a chord tone's id
 * carries a `.` suffix and CSS reads that as a class. Ids the host does not hold are left
 * out rather than reported: a page the score is not currently showing is the ordinary case.
 */
export function boxesFor(host: BoxHost, ids: Iterable<string>): Map<string, Box> {
  const origin = host.getBoundingClientRect();
  const scale = scaleOf(host, origin);
  const boxes = new Map<string, Box>();
  for (const id of ids) {
    const element = host.querySelector(`[id="${cssEscape(id)}"]`);
    if (element === null) continue;
    const rect = element.getBoundingClientRect();
    boxes.set(id, {
      x: (rect.x - origin.x) / scale,
      y: (rect.y - origin.y) / scale,
      width: rect.width / scale,
      height: rect.height / scale,
    });
  }
  return boxes;
}

/** Quote the characters an attribute selector would otherwise read as syntax. */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, "\\$&");
}

/** The smallest box containing a point, or `null` when the point is outside all of them. */
export function boxAt(boxes: ReadonlyMap<string, Box>, point: Point): string | null {
  let best: string | null = null;
  let bestArea = Infinity;
  for (const [id, box] of boxes) {
    if (!contains(box, point)) continue;
    const area = box.width * box.height;
    if (area < bestArea) {
      best = id;
      bestArea = area;
    }
  }
  return best;
}

function contains(box: Box, point: Point): boolean {
  return (
    point.x >= box.x &&
    point.x <= box.x + box.width &&
    point.y >= box.y &&
    point.y <= box.y + box.height
  );
}

/**
 * The box nearest a point, within a radius.
 *
 * A note head is a small target and a click that misses it by two pixels means the same
 * thing as one that hits. Distance is measured to the edge of the box, so a point inside
 * one is at distance zero and this agrees with {@link boxAt} wherever both answer.
 */
export function boxNear(
  boxes: ReadonlyMap<string, Box>,
  point: Point,
  radius: number,
): string | null {
  let best: string | null = null;
  let bestDistance = radius;
  for (const [id, box] of boxes) {
    const distance = distanceTo(box, point);
    if (distance <= bestDistance) {
      best = id;
      bestDistance = distance;
    }
  }
  return best;
}

function distanceTo(box: Box, point: Point): number {
  const dx = Math.max(box.x - point.x, 0, point.x - (box.x + box.width));
  const dy = Math.max(box.y - point.y, 0, point.y - (box.y + box.height));
  return Math.hypot(dx, dy);
}

/** The smallest box containing all of the given ones, or `null` when there are none. */
export function unionOf(boxes: Iterable<Box>): Box | null {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const box of boxes) {
    left = Math.min(left, box.x);
    top = Math.min(top, box.y);
    right = Math.max(right, box.x + box.width);
    bottom = Math.max(bottom, box.y + box.height);
  }
  if (left === Infinity) return null;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Grow a box by a margin on every side, for drawing a selection outline around it. */
export function padded(box: Box, margin: number): Box {
  return {
    x: box.x - margin,
    y: box.y - margin,
    width: box.width + margin * 2,
    height: box.height + margin * 2,
  };
}
